/**
 * L'avvio a richiesta, raccontato (06-RIPRODUZIONE §2 e §4).
 *
 * La piattaforma avvia molti canali a richiesta: il processo di transcodifica nasce
 * quando il primo spettatore lo chiede. Fra «ho premuto OK» e «vedo qualcosa» possono
 * passare secondi, e qui la rotella è sbagliata — è indistinguibile da una rotella
 * eterna, e dopo sette secondi la persona preme Indietro e riprova, peggiorando la
 * situazione.
 *
 * Lo sbaglio già visto su questa piattaforma e da non rifare: **misurare l'attesa in
 * «controlli» invece che in secondi.** «Riprovo dieci volte» è un tempo che dipende da
 * quanto è lenta la rete, cioè non è un tempo. Qui si contano millisecondi.
 */

export type StartupPhase =
  | 'starting'
  | 'warming_up'
  | 'slower_than_usual'
  | 'failed';

export const STARTUP_THRESHOLDS_MS = {
  warmingUp: 3_000,
  slowerThanUsual: 8_000,
  failed: 15_000,
} as const;

export function startupPhase(elapsedMs: number): StartupPhase {
  if (elapsedMs >= STARTUP_THRESHOLDS_MS.failed) return 'failed';
  if (elapsedMs >= STARTUP_THRESHOLDS_MS.slowerThanUsual) return 'slower_than_usual';
  if (elapsedMs >= STARTUP_THRESHOLDS_MS.warmingUp) return 'warming_up';
  return 'starting';
}

/**
 * La stima «di solito ci vogliono ~8 secondi» la dice il server, non l'app.
 *
 * Se il backend sa che questo canale in media ci mette otto secondi, lo manda; se non lo
 * sa, l'app **non inventa un numero**. Meglio nessuna stima che una stima sbagliata: una
 * stima inventata è un ripiego silenzioso travestito da gentilezza.
 */
export function startupEstimateSeconds(serverEstimateMs: number | null | undefined): number | null {
  if (typeof serverEstimateMs !== 'number' || !Number.isFinite(serverEstimateMs)) return null;
  if (serverEstimateMs <= 0) return null;
  return Math.round(serverEstimateMs / 1000);
}

/**
 * Quando un canale è guardabile (06-RIPRODUZIONE §3).
 *
 * Una sessione in stato `running` **non** vuol dire che il canale si veda: vuol dire che
 * il processo è vivo. Il criterio è uno solo, ed è misurabile dal dispositivo: la
 * playlist risponde, contiene almeno due segmenti, e il più recente è fresco. Nessuno
 * stato riportato dal server sostituisce questa verifica.
 */
export const MIN_SEGMENTS = 2;

export interface PlaylistProbe {
  readonly segmentCount: number;
  /** Durata dichiarata dell'ultimo segmento, in secondi (`#EXTINF`). */
  readonly lastSegmentDurationSec: number;
  /** Da quanto la playlist non cambia, in millisecondi. */
  readonly ageMs: number;
}

/**
 * «Fresco» vuol dire: la playlist non è più vecchia di tre segmenti. Tre e non uno
 * perché un aggiornamento in ritardo di un segmento è normale, e un canale dichiarato
 * morto a ogni singhiozzo è un canale che non parte mai.
 */
export function isWatchable(probe: PlaylistProbe): boolean {
  if (probe.segmentCount < MIN_SEGMENTS) return false;
  if (probe.lastSegmentDurationSec <= 0) return false;
  return probe.ageMs <= probe.lastSegmentDurationSec * 3 * 1000;
}

/**
 * Il recupero da una caduta, in ordine (06-RIPRODUZIONE §7).
 *
 * Fermarsi è un passo del procedimento, non la sua assenza: un'app che riprova per sempre
 * tiene una connessione occupata su una linea che ne ha due, e l'utente non capisce
 * perché il telefono non entra più.
 */
export type RecoveryStep =
  | { readonly action: 'resume'; readonly delayMs: number; readonly showMessage: false }
  | { readonly action: 'reloadPlaylist'; readonly delayMs: number; readonly showMessage: true }
  | { readonly action: 'restart'; readonly delayMs: number; readonly showMessage: true }
  | { readonly action: 'giveUp'; readonly delayMs: 0; readonly showMessage: true };

const RECOVERY_PLAN: ReadonlyArray<RecoveryStep> = [
  { action: 'resume', delayMs: 1_000, showMessage: false },
  { action: 'resume', delayMs: 1_000, showMessage: false },
  { action: 'reloadPlaylist', delayMs: 2_000, showMessage: true },
  { action: 'reloadPlaylist', delayMs: 4_000, showMessage: true },
  { action: 'restart', delayMs: 0, showMessage: true },
  { action: 'giveUp', delayMs: 0, showMessage: true },
];

export function recoveryStep(attempt: number): RecoveryStep {
  const last = RECOVERY_PLAN[RECOVERY_PLAN.length - 1] as RecoveryStep;
  if (attempt < 0) return last;
  return RECOVERY_PLAN[attempt] ?? last;
}

/**
 * Attesa prima di avviare, nello zapping (06-RIPRODUZIONE §5).
 *
 * Chi tiene premuto ▼ passa sopra venti canali: non vanno avviate venti sessioni. Si
 * aspetta che il dito si fermi e solo allora si chiede il canale. Senza questo, lo
 * zapping è un attacco alla propria infrastruttura.
 */
export const ZAP_SETTLE_MS = 400;

/** Circolare, e non esce dalla categoria in cui si era (prototipo v3, `zap`). */
export function zapIndex(currentIndex: number, delta: number, total: number): number {
  if (total <= 0) return 0;
  return ((currentIndex + delta) % total + total) % total;
}
